
import config from '@/config';
import InvisibleRecaptcha from 'vue-invisible-recaptcha';
import router from '@/router';
import authorizeUser from '@/logic/auth';
import EazyHeading from '@/components/EazyHeading.vue';
import BlueCheckbox from '@/components/BlueCheckbox.vue';
import { getLocaleString } from '@/logic/requestBeApi';
import timezones from '@/logic/helpers/timezones/timezones';
import formValidators from '@/logic/helpers/forms/validators';
import inputValidators from '@/logic/helpers/inputs/validators';
import ServerResponseAlert from '@/components/ServerResponseAlert.vue';
import passDataToResponseAlert from '@/logic/helpers/api/passDataToResponseAlert';
import requestAPI from '@/logic/requestBeApi';

export default {
  name: 'Registration',
  components: {
    InvisibleRecaptcha,
    EazyHeading,
    BlueCheckbox,
    ServerResponseAlert,
  },

  data(): Record<string, unknown> {
    return {
      router,
      registrationData: {
        email: undefined,
        password: undefined,
      },
      validationRules: {
        email: [{ validator: inputValidators.email, trigger: 'blur' }],
        password: [{ validator: inputValidators.password, trigger: 'blur' }],
      },
      processingRegistrationRequest: false,
      takenEmail: false,
      anotherError: false,
      refToken: this.$route.query.ref_token || undefined,
      newsletterAcceptted: false,
      regulationsAccepted: false,
      termsAccepted: false,
      renderRecaptcha: false,
      registrationAttempted: false,
      weakPasswordError: '',
      formIsValid: false,
      registeredUserResponsesFromBE: [
        'User onboarding state mismatch',
        'This User is already registerd',
      ],
      serverResponse: null,
      name: '',
      roleFromButtonClick: '',
    };
  },

  computed: {
    selectedRole(): string {
      return this.isAssessorPlus ? 'client' : this.roleFromButtonClick;
    },

    isAssessorPlus(): boolean {
      return localStorage.getItem('roleType') === 'assessorPlus';
    },

    prodEnv(): boolean {
      return window.location.href.includes('app.');
    },

    userSelectedRole(): boolean {
      return this.selectedRole === 'assessor' || this.selectedRole === 'client';
    },

    legalsNotAccepted(): boolean {
      if (this.isAssessorPlus) {
        return this.regulationsAccepted && this.termsAccepted;
      } else {
        return !this.regulationsAccepted && !this.termsAccepted;
      }
    },
  },

  mounted(): void {
    setTimeout(() => {
      this.renderRecaptcha = true;
    }, 1000);
  },

  created(): void {
    if (this.isAssessorPlus) {
      this.selectedRole === 'client';
    }
  },

  methods: {
    handleRoleButtonClick(role): void {
      if (!this.isAssessorPlus) {
        this.roleFromButtonClick = role;
      }
    },

    generateRandomPassword(): string {
      const length = 6;
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

      let randomPassword = '';
      let hasDigit = false;
      let hasLowercaseLetter = false;
      let hasUppercaseLetter = false;

      for (let i = 0; i < length; i++) {
        const char = chars.charAt(Math.floor(Math.random() * chars.length));
        randomPassword += char;

        if (!hasDigit && /[0-9]/.test(char)) {
          hasDigit = true;
        }
        if (!hasLowercaseLetter && /[a-z]/.test(char)) {
          hasLowercaseLetter = true;
        }
        if (!hasUppercaseLetter && /[A-Z]/.test(char)) {
          hasUppercaseLetter = true;
        }
      }
      if (hasLowercaseLetter && hasDigit && hasUppercaseLetter) {
        return randomPassword;
      } else {
        return this.generateRandomPassword();
      }
    },

    async isClientAlreadyInFavs(): Promise<boolean> {
      const resp = await requestAPI({
        url: '/api/v1/favourites/clients',
        method: 'GET',
      });
      if (resp.ok) {
        const data = await resp.json();
        const clients = data;
        if (
          clients.some((client: { email: string }) => client.email === this.registrationData.email)
        ) {
          this.$message.error(this.$t('clientAlreadyInDatabase'));
          return true;
        }
        this.$message.success(this.$t('clientRegistrationsuccessfull'));
        return false;
      } else {
        this.$message.error(this.$t('someErrorOccurred'));
        return false;
      }
    },

    saveClientDataToLS(): void {
      localStorage.setItem('clientEmail', this.registrationData.email);
      localStorage.setItem('clientName', this.name);
    },

    validate(): void {
      this.registrationAttempted = true;

      formValidators.validateForm(this, 'registrationForm');
    },

    async registerUser(e: { preventDefault: () => void }): Promise<void> {
      let isClientInFavs = false;
      if (this.isAssessorPlus) {
        isClientInFavs = await this.isClientAlreadyInFavs();
        this.registrationData.password = this.generateRandomPassword();
      }
      if (!this.prodEnv) e.preventDefault();
      if (!this.prodEnv) this.validate();

      if (this.legalsNotAccepted || !this.formIsValid || isClientInFavs) return;

      this.serverResponse = {};
      this.processingRegistrationRequest = true;

      const regApiUrl = this.isAssessorPlus
        ? `${config.BE_URL}/api/v1/onboard-authenticated`
        : `${config.BE_URL}/api/v1/onboard${this.refToken ? '?ref_token=' + this.refToken : ''}`;
      const headers = {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
        'X-LOCALE': getLocaleString(),
        ...(this.isAssessorPlus && { Authorization: `Bearer ${localStorage.getItem('token')}` }),
      };

      if (config.isOnDevEnv) delete headers['X-LOCALE'];

      const response = await fetch(regApiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          password: this.registrationData.password,
          user_type: this.selectedRole,
          email: this.registrationData.email,
          newsletter: this.newsletterAcceptted,
        }),
      });
      if (this.isAssessorPlus) {
        const data = await response.json();
        const id = data.id;
        localStorage.setItem('clientID', id);
        localStorage.setItem('userType', this.selectedRole);
      }
      if (response.ok) {
        const auth = await authorizeUser(
          this.isAssessorPlus
            ? localStorage.getItem('assessorPlusEmail')
            : this.registrationData.email,
          this.isAssessorPlus
            ? localStorage.getItem('assessorPlusPassword')
            : this.registrationData.password,
        );
        if (auth.ok) {
          if (this.isAssessorPlus) {
            this.saveClientDataToLS();
            router.push('/client-onboarding');
          } else {
            localStorage.setItem('onboardingOngoing', 'true');
            this.showHoverPopupExplainerOnTheFirstVisit();
            timezones.updateServerWithUserTimezone();
            router.push(`/${this.selectedRole}-onboarding`);
          }
        } else {
          this.anotherError = true;
        }
      } else {
        const data = await passDataToResponseAlert(response);
        this.serverResponse = data;
      }

      this.processingRegistrationRequest = false;
    },

    showHoverPopupExplainerOnTheFirstVisit(): void {
      const userAlreadyGotHoverPopupExplainer = !!localStorage.getItem(
        'userAlreadyGotHoverPopupExplainer',
      );

      if (userAlreadyGotHoverPopupExplainer) return;

      this.$notify.info({
        title: this.$t('info'),
        message: this.$t('hoverPopupExplainer'),
        duration: 11000,
        offset: 120,
      });

      localStorage.setItem('userAlreadyGotHoverPopupExplainer', 'true');
    },
  },
};
